/**
 * "Keep me signed in on this device" (owner decision 31 Oct 2026). Offered only in the installed portal app
 * (display-mode standalone). The tick is remembered on this device; right after sign-in the app asks the API to
 * lengthen this session (POST /v1/identity/session/keep-signed-in: 24 h idle / 7 days, customer and agency only,
 * within 10 minutes of sign-in). Sign-out clears device storage, so the tick has to be given again.
 */
import type { Me } from '@qm/contracts/identity';
import { bffJson } from './bff';

export const KEEP_SIGNED_IN_KEY = 'qm.keepSignedIn';

export function isInstalledApp(): boolean {
  try {
    if (typeof matchMedia === 'function' && (matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: window-controls-overlay)').matches)) return true;
    return (globalThis.navigator as { standalone?: boolean } | undefined)?.standalone === true; // iOS home-screen app
  } catch { return false; }
}

export function keepSignedInWanted(): boolean {
  try { return localStorage.getItem(KEEP_SIGNED_IN_KEY) === '1'; } catch { return false; }
}

export function setKeepSignedInWanted(on: boolean): void {
  try { if (on) localStorage.setItem(KEEP_SIGNED_IN_KEY, '1'); else localStorage.removeItem(KEEP_SIGNED_IN_KEY); } catch { /* storage blocked */ }
}

/** After sign-in: lengthen this session when the person asked for it in the installed app. Never throws. */
export async function applyKeepSignedIn(me: Me, installed = isInstalledApp(), wanted = keepSignedInWanted()): Promise<Me> {
  if (!installed || !wanted || me.realm === 'staff' || me.session.keepSignedIn) return me;
  if (me.mfa.state === 'pending' || me.mfa.state === 'enrol_required') return me;
  try {
    const r = await bffJson<{ keepSignedIn: boolean; idleExpiresAt: string; absoluteExpiresAt: string }>('/v1/identity/session/keep-signed-in', 'POST', {});
    return { ...me, session: { ...me.session, keepSignedIn: true, idleExpiresAt: r.idleExpiresAt, absoluteExpiresAt: r.absoluteExpiresAt } };
  } catch { return me; }
}
